import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Loader2, RefreshCw, Users, Cpu, CalendarClock, MessageCircle, Database, BarChart3 } from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { aiApi } from "./aiApi";
import AILLMSettings from "./AILLMSettings";
import AIAtlasSettings from "./AIAtlasSettings";
import AIWhatsAppSettings from "./AIWhatsAppSettings";
import { AIUsagePanel, AIMemoryPanel } from "./AIUsagePanel";
import AIBubbleVisibility from "./AIBubbleVisibility";
import AIImageSettings from "./AIImageSettings";
import AIWebsiteDesignerSettings from "./AIWebsiteDesignerSettings";
import { celebrateMickey } from "./mickeyMood";
import AISettingsGroup, { useOpenGroup } from "./AISettingsGroup";

const DAYS = [[6, "Ahd"], [0, "Isn"], [1, "Sel"], [2, "Rab"], [3, "Kha"], [4, "Jum"], [5, "Sab"]];

export default function AIAutomation() {
  const [d, setD] = useState(null);
  const [date, setDate] = useState("");
  const [running, setRunning] = useState(null);
  const [openGroup, toggleGroup] = useOpenGroup();
  const load = () => aiApi.automation().then(setD).catch(() => toast.error("Gagal memuat tetapan"));
  useEffect(() => { load(); }, []);

  const save = async (patch, msg) => {
    const s = await aiApi.saveAutomation(patch);
    setD((x) => ({ ...x, settings: s }));
    toast.success(msg || "Tetapan disimpan");
  };
  const run = async (job) => {
    setRunning(job);
    try { const r = await aiApi.runJob(job, date); toast.success(r.message); celebrateMickey(); setTimeout(load, 4000); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setRunning(null); }
  };

  if (!d) return <div className="grid place-items-center h-40"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const s = d.settings;
  return (
    <div className="h-full overflow-y-auto p-3 space-y-2" data-testid="ai-automation">
      <div className="px-1 pb-1 text-[11px] text-slate-400">Tekan setiap kumpulan untuk buka/tutup tetapan.</div>
      <AISettingsGroup id="access" icon={Users} tone="text-pink-600 bg-pink-50" title="Peranan & Akses" hint="Butang Mimi, push notifikasi, WhatsApp AI" open={openGroup === "access"} onToggle={toggleGroup}>
        <AIBubbleVisibility />
      </AISettingsGroup>
      <AISettingsGroup id="model" icon={Cpu} tone="text-fuchsia-600 bg-fuchsia-50" title="Model AI, Image & Website Designer" hint="Penyedia, kunci API, model, gambar, landing page" open={openGroup === "model"} onToggle={toggleGroup}>
        <AILLMSettings />
        <AIWebsiteDesignerSettings />
        <AIImageSettings />
      </AISettingsGroup>
      <AISettingsGroup id="schedule" icon={CalendarClock} tone="text-teal-600 bg-teal-50" title="Jadual & Hari Bekerja" hint="Tugasan automatik, peringatan yuran, hari kerja"
        badge={`${Object.values(s.jobs || {}).filter(Boolean).length}/${Object.keys(d.labels).length} aktif`} open={openGroup === "schedule"} onToggle={toggleGroup}>
        <div className="rounded-2xl border border-slate-100 bg-white p-3">
          <div className="font-extrabold text-sm text-slate-800 mb-2">Jadual Peringatan Mimi Junior</div>
          {Object.entries(d.labels).map(([k, label]) => (
            <div key={k} className="flex items-center gap-2 py-1.5 border-t border-slate-50 first:border-0">
              <Switch checked={!!s.jobs[k]} onCheckedChange={(v) => save({ jobs: { ...s.jobs, [k]: v } })} data-testid={`ai-job-switch-${k}`} />
              <div className="flex-1 text-[13px] text-slate-700">{label}</div>
              <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={() => run(k)} disabled={running === k} data-testid={`ai-job-run-${k}`}>
                {running === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />} Jalan
              </Button>
            </div>
          ))}
          <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="ai-fee-reminder-days-row">
            <span className="text-[11px] text-slate-500">Peringatan yuran dihantar</span>
            <Input type="number" min={1} max={14} defaultValue={s.fee_reminder_days || 3} key={s.fee_reminder_days}
              onBlur={(e) => { const v = Math.min(14, Math.max(1, parseInt(e.target.value, 10) || 3)); if (v !== s.fee_reminder_days) save({ fee_reminder_days: v }, `Peringatan yuran: ${v} hari sebelum tarikh akhir`); }}
              className="h-7 text-xs w-16" data-testid="ai-fee-reminder-days" />
            <span className="text-[11px] text-slate-500">hari sebelum tarikh akhir</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[11px] text-slate-500">Tarikh simulasi (pilihan):</span>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-7 text-xs w-36" data-testid="ai-job-date" />
          </div>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-3">
          <div className="font-extrabold text-sm text-slate-800 mb-2">Hari Bekerja</div>
          <div className="flex flex-wrap gap-1">
            {DAYS.map(([v, l]) => {
              const on = s.work_days.includes(v);
              return <button key={v} onClick={() => save({ work_days: on ? s.work_days.filter((x) => x !== v) : [...s.work_days, v] })} data-testid={`ai-workday-${v}`}
                className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${on ? "bg-teal-500 text-white border-teal-500" : "bg-white text-slate-500 border-slate-200 hover:border-teal-300"}`}>{l}</button>;
            })}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Switch checked={!!s.llm_compose} onCheckedChange={(v) => save({ llm_compose: v })} data-testid="ai-llm-compose-switch" />
            <span className="text-[13px] text-slate-700">Ayat pembuka mesra dijana AI</span>
          </div>
          <div className="text-[11px] text-slate-400 mt-2">Peringatan harian dilangkau pada hari cuti (cuti umum / cuti penggal dalam Takwim).</div>
        </div>
      </AISettingsGroup>
      <AISettingsGroup id="whatsapp" icon={MessageCircle} tone="text-emerald-600 bg-emerald-50" title="Laporan WhatsApp" hint="Penerima, nombor Wabot, mod ujian" open={openGroup === "whatsapp"} onToggle={toggleGroup}>
        <AIWhatsAppSettings settings={s} branches={d.branches || []} onSave={save} />
      </AISettingsGroup>
      <AISettingsGroup id="data" icon={Database} tone="text-sky-600 bg-sky-50" title="Memori & Data AI" hint="Memori pilihan Mimi, MongoDB Atlas" open={openGroup === "data"} onToggle={toggleGroup}>
        <AIMemoryPanel />
        <AIAtlasSettings />
      </AISettingsGroup>
      <AISettingsGroup id="usage" icon={BarChart3} tone="text-amber-600 bg-amber-50" title="Penggunaan & Log Larian" hint="Statistik token/kos, sejarah larian tugasan" open={openGroup === "usage"} onToggle={toggleGroup}>
        <AIUsagePanel />
        <div className="rounded-2xl border border-slate-100 bg-white p-3">
          <div className="flex items-center mb-2"><div className="font-extrabold text-sm text-slate-800 flex-1">Log Larian</div>
            <button onClick={load} className="text-slate-400 hover:text-slate-600" data-testid="ai-runs-refresh"><RefreshCw className="h-4 w-4" /></button></div>
          {d.runs.length === 0 && <div className="text-xs text-slate-400">Belum ada larian.</div>}
          {d.runs.map((r) => (
            <div key={r.id + r.started_at} className="text-[11px] py-1.5 border-t border-slate-50 first:border-0 flex items-start gap-2">
              <Badge variant="outline" className={`text-[10px] ${r.status === "done" ? "border-emerald-300 text-emerald-700" : r.status === "error" ? "border-rose-300 text-rose-700" : "text-amber-700"}`}>{r.status}</Badge>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-700">{d.labels[r.job] || r.job} • {r.date}</div>
                <div className="text-slate-400 truncate">{r.triggered_by} • {r.stats ? JSON.stringify(r.stats) : r.error || ""}</div>
              </div>
            </div>
          ))}
        </div>
      </AISettingsGroup>
    </div>
  );
}
